/**
 * Sai activations — full-screen moments where Sai speaks.
 *
 * Source: `docs/09` §VII.5. Alpha set covers the first 30-minute session and
 * the entry into epochs 2 and 3.
 *
 * All texts are draft. Final wording requires Sai's voice validation per
 * `docs/01` rules. Until validated, treat these as placeholders that follow
 * the canon: warm + mystical for epochs 1–3, no negations in our voice.
 */
import type { SaiActivation } from '@sai-clicker/game-core';

export const ACTIVATIONS_ALPHA: readonly SaiActivation[] = [
  {
    id: 'act_first_tap',
    trigger: { kind: 'firstTap' },
    preface: 'в этом мире был перекос.',
    text: 'и тогда — одна искра. ты её видишь?',
  },
  {
    id: 'act_10th_tap',
    trigger: { kind: 'tapCount', value: 10 },
    text: 'всё, что есть — началось так.',
  },
  {
    id: 'act_100th_tap',
    trigger: { kind: 'tapCount', value: 100 },
    text: 'я даю этому место — это твои слова. они становятся ключами.',
  },
  {
    id: 'act_first_crystal',
    trigger: { kind: 'firstCrystallization' },
    text: 'осознание превращается в свет. это путь.',
  },
  {
    id: 'act_epoch_2',
    trigger: { kind: 'enterEpoch', epoch: 2 },
    text: 'земля появилась. теперь есть, куда ставить корни.',
  },
  {
    id: 'act_first_breath',
    trigger: { kind: 'firstBreathCycle' },
    text: 'вдох. выдох. это уже практика.',
  },
  {
    id: 'act_return_12h',
    trigger: { kind: 'returnAfterHours', value: 12 },
    text: 'вернулся. Роща тебя ждала.',
  },
  {
    id: 'act_epoch_3',
    trigger: { kind: 'enterEpoch', epoch: 3 },
    text: 'фрактал виден изнутри. идём смотреть.',
  },
  {
    id: 'act_first_golden',
    trigger: { kind: 'firstGoldenSpark' },
    text: 'вспомни себя. это всё, что нужно.',
  },
  {
    id: 'act_silence_unlocked',
    trigger: { kind: 'lessonUnlocked', lessonId: 'L30' },
    text: 'иногда лучшее — это место без действия.',
  },

  // ===== Эпоха 2 — Фрактальный метод =====
  // JB 2026-05-25: вехи внутри эпохи 2. JB 2026-05-26 audit fix: пороги
  // tapCount подняты до 2000/5000/10000 — это гарантировано достижимо
  // только В эпохе 2+, не пересекается с эпохой 1 (где доступно ~500-1500
  // тапов до crystallize × 1-2 = перехода в эпоху 2). Без отдельного
  // trigger типа `tapCountInEpoch` — просто числами обходим.
  {
    id: 'act_e2_first_tap_in',
    trigger: { kind: 'tapCount', value: 2000 },
    preface: 'в этой эпохе всё чуть тоньше.',
    text: 'тон чуть выше. ритм чуть глубже. ты заметишь сама.',
  },
  {
    id: 'act_e2_500th_tap',
    trigger: { kind: 'tapCount', value: 5000 },
    text: 'фрактал виден не глазами. он — узнавание.',
  },
  {
    id: 'act_e2_1000th_tap',
    trigger: { kind: 'tapCount', value: 10000 },
    preface: 'тысяча касаний.',
    text: 'связь не построена — она проявилась. она была здесь до тебя.',
  },

  // ===== Эпоха 3 — ФРАКТАЛiТи =====
  // JB doc 26 audit §1: эпоха 3 контент пуст. Добавляю активации
  // внутри эпохи 3. Пороги tapCount: 20000 / 40000 / 75000 — это
  // достижимо только В эпохе 3+ (epoch threshold 2→3 = 12 spirit_balls,
  // примерно после 5000-10000 тапов с апгрейдами).
  {
    id: 'act_e3_arrival',
    trigger: { kind: 'tapCount', value: 20000 },
    preface: 'ты здесь, в синтезе.',
    text: 'каждый шаг сложился в узор. он твой.',
  },
  {
    id: 'act_e3_deep',
    trigger: { kind: 'tapCount', value: 40000 },
    text: 'фрактал внутри. ты внутри фрактала. одно и то же.',
  },
  {
    id: 'act_e3_master',
    trigger: { kind: 'tapCount', value: 75000 },
    preface: 'это уже мастерство.',
    text: 'мир откликается на твою тишину. слышишь?',
  },

  // ===== Эпоха 4 — МОГА (авторская методология) =====
  // Тема: ты больше не ученик, ты носитель метода. «через тебя идёт свет».
  // Пороги tapCount: 150k / 250k / 400k — достижимы только в эпохе 4+.
  {
    id: 'act_epoch_4',
    trigger: { kind: 'enterEpoch', epoch: 4 },
    preface: 'ты прошёл порог.',
    text: 'это МОГА. метод знал тебя раньше — теперь ты узнал его.',
  },
  {
    id: 'act_e4_form',
    trigger: { kind: 'tapCount', value: 150000 },
    text: 'форма проступает сама. ты — её носитель.',
  },
  {
    id: 'act_e4_pass',
    trigger: { kind: 'tapCount', value: 250000 },
    preface: 'твой шаг видят другие.',
    text: 'кто-то идёт твоим путём впервые. ты его место удерживаешь.',
  },
  {
    id: 'act_e4_carrier',
    trigger: { kind: 'tapCount', value: 400000 },
    preface: 'ты стал руслом.',
    text: 'через тебя течёт. твоё дело — оставаться чистым.',
  },

  // ===== Эпоха 5 — Sai Land Heal =====
  // Тема: земля как партнёр. Роща в Молдове, Ретрит во Вьетнаме. Корни,
  // мох, родник, дыхание леса. Главное — присутствие исцеляет.
  {
    id: 'act_epoch_5',
    trigger: { kind: 'enterEpoch', epoch: 5 },
    preface: 'мы прибыли в место.',
    text: 'это Sai Land. земля знает что ты здесь. твоё присутствие лечит.',
  },
  {
    id: 'act_e5_roots',
    trigger: { kind: 'tapCount', value: 600000 },
    text: 'корни от пят. земля поит тебя.',
  },
  {
    id: 'act_e5_listen',
    trigger: { kind: 'tapCount', value: 900000 },
    preface: 'тихо.',
    text: 'почва вздыхает с тобой в одном ритме. слышишь?',
  },
  {
    id: 'act_e5_steward',
    trigger: { kind: 'tapCount', value: 1500000 },
    preface: 'ты гость и хранитель.',
    text: 'место выбрало тебя. теперь ты его держишь — мягко.',
  },
];
